import React, { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '@store/authStore';
import { apiClient } from '@/lib/api';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@components/ui/Card';
import { EmptyState } from '@assets/images';
import { Skeleton } from '@components/ui/Skeleton';
import { QuoteCard, QuoteFilters, QuoteDetailsDrawer, QuoteSearchBar } from '../components';
import { RiArrowDownLine, RiLoader4Line } from '@remixicon/react';
import { usePermissions } from '@/hooks/usePermissions';
import { MODULES, SCOPES } from '@/lib/permissions';
import {
  searchQuotesByDate,
  searchQuotesByAirport,
  searchQuotesByClient,
} from '../services/quoteSearchService';

const { SEARCH_TYPES } = QuoteSearchBar;

const PAGE_SIZE = 10;

/**
 * QuotesList Page
 * Main page displaying all quotes with filtering functionality
 */
const QuotesList = () => {

  // State management
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { userProfile, isLoading: isAuthLoading, isAuthenticated, fetchUserProfile } = useAuthStore();

  // Search-by state (mirrors the mobile QuotesScreen behaviour).
  // When `searchType` is not 'none' the infinite query swaps to the matching
  // searchQuotesBy* helper, keyed by the chosen `searchValue`.
  const [searchType, setSearchType] = useState(SEARCH_TYPES.NONE);
  const [searchValue, setSearchValue] = useState(null);
  const [searchLabel, setSearchLabel] = useState('');

  // Catchup now runs on /track (where the on-screen flight rows are the
  // source of truth). The /quotes list doesn't carry flight data, so there's
  // nothing to feed the hook here. When the broker visits /track, the
  // catchup will update flight statuses + cascade to quote statuses —
  // those changes are picked up by /quotes via React Query invalidation.

  // Ensure user profile is loaded when authenticated but profile is missing
  useEffect(() => {
    if (isAuthenticated && !userProfile && !isAuthLoading) {
      fetchUserProfile();
    }
  }, [isAuthenticated, userProfile, isAuthLoading, fetchUserProfile]);

  // Get profile ID (API may return as 'id' or 'userprofile_id')
  const profileId = userProfile?.userprofile_id;
  const orgId = userProfile?.org_id;

  // for testing cameron profile id
  //const profileId = "b3492843-679c-4d18-8f78-6b1ea68b5393";

  // Per-role data scope from the permission matrix.
  // self → broker sees only their own quotes (created_by)
  // global → admin/finance/coordinator/compliance see all quotes in the org (broker_org_id)
  const { scope: getScope } = usePermissions();
  const bookingScope = getScope(MODULES.BOOKING);
  const scopeFilter = useMemo(() => {
    if (bookingScope === SCOPES.GLOBAL && orgId) return { broker_org_id: orgId };
    if (bookingScope === SCOPES.SELF && profileId) return { created_by: profileId };
    return null;
  }, [bookingScope, orgId, profileId]);

  /**
   * Use React Query to fetch quotes with API-based filtering
   * - Fetches based on activeFilter status
   * - Refetches when activeFilter changes
   */
  const {
    data: quotesPages,
    isLoading: isQuotesLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error: quotesError,
  } = useInfiniteQuery({
    // Include searchType + searchValue so changing the search starts a fresh
    // paginated query from page 0.
    queryKey: ['quotes', 'list', bookingScope, profileId, orgId, searchType, searchValue],
    queryFn: async ({ pageParam = 0 }) => {
      if (searchType === SEARCH_TYPES.DATE && searchValue) {
        return await searchQuotesByDate(searchValue, scopeFilter || {}, pageParam, PAGE_SIZE);
      }
      if (searchType === SEARCH_TYPES.AIRPORT && searchValue) {
        return await searchQuotesByAirport(searchValue, scopeFilter || {}, pageParam, PAGE_SIZE);
      }
      if (searchType === SEARCH_TYPES.CLIENT && searchValue) {
        return await searchQuotesByClient(searchValue, scopeFilter || {}, pageParam, PAGE_SIZE);
      }
      const data = await apiClient.getQuotes(pageParam, PAGE_SIZE, {
        ...scopeFilter,
        sort_by: 'created_at',
        sort_order: 'desc',
      });
      const arr = Array.isArray(data) ? data : [];
      return { data: arr, hasMore: arr.length === PAGE_SIZE };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.hasMore) return undefined;
      return allPages.length * PAGE_SIZE;
    },
    enabled: !!scopeFilter,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  // All loaded quotes from the infinite query (default OR search mode).
  // Normalised id/status; globally deduped by quote_id since the date/airport
  // OR-via-two-calls path can return the same quote across pages.
  const isSearchActive = searchType !== SEARCH_TYPES.NONE;
  const allQuotes = useMemo(() => {
    const flat = (quotesPages?.pages || []).flatMap((p) => (p?.data || []));
    const seen = new Set();
    const out = [];
    for (const q of flat) {
      const id = q.quote_id || q.id;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push({ ...q, id: q.quote_id, status: q.status || 'draft' });
    }
    return out;
  }, [quotesPages]);

  // Apply the active tab filter for display.
  const quotesData = useMemo(() => {
    if (activeFilter === 'all') return allQuotes;
    if (activeFilter === 'draft') {
      return allQuotes.filter(
        (q) => q.status === 'DRAFT' || q.status === 'draft' || !q.status
      );
    }
    return allQuotes.filter(
      (q) => (q.status || '').toLowerCase() === activeFilter.toLowerCase()
    );
  }, [allQuotes, activeFilter]);

  const statusCounts = useMemo(() => {
    const counts = { all: allQuotes.length };
    allQuotes.forEach((q) => {
      const s = (q.status || 'draft').toLowerCase();
      counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  }, [allQuotes]);

  /**
   * Handle filter change
   */
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  /**
   * Handle a "Search By" selection from the QuoteSearchBar.
   * Setting `searchType`/`searchValue` re-keys the infinite query, which
   * automatically resets pagination and fetches page 0 in the chosen mode.
   */
  const handleSearchSelect = (type, value, label) => {
    if (!type || !value) return;
    setSearchType(type);
    setSearchValue(value);
    setSearchLabel(label || '');
  };

  const handleSearchClear = () => {
    setSearchType(SEARCH_TYPES.NONE);
    setSearchValue(null);
    setSearchLabel('');
  };

  /**
   * Handle viewing a quote
   */
  const handleViewQuote = (quote) => {
    setSelectedQuoteId(quote.id);
    setIsDrawerOpen(true);
  };

  return (
    <div className="pb-20 bg-white/50 backdrop-blur-sm rounded-[2rem] p-4 md:p-6">

      {/* Main Content */}
      <div className="w-full">
        <Card className="overflow-hidden !border-gray-100 !bg-transparent !shadow-none !border-0">
          <CardContent className="!p-1">
            {/* Filter + Search By — side by side on md+, stacked on narrow screens */}
            <div className="mb-6 flex flex-col items-center gap-3 w-full max-w-5xl mx-auto">
              {/* Desktop: filter takes the remaining row width on the left,
                  search bar sits at content width on the right.
                  Mobile (flex-col): filter on top, search below. */}
              <div className="flex flex-col items-center md:flex-row md:items-center gap-3 md:gap-6 w-full">
                {/* `md:flex-1` lets the filter pill grow to fill the row beside
                    the search bar — matches the original wide-pill look. */}
                <div className="w-full md:flex-1 md:min-w-0">
                  <QuoteFilters
                    activeFilter={activeFilter}
                    onFilterChange={handleFilterChange}
                    totalCount={quotesData?.length}
                    statusCounts={statusCounts}
                  />
                </div>
                <div className="shrink-0">
                  <QuoteSearchBar
                    searchType={searchType}
                    searchValue={searchValue}
                    searchLabel={searchLabel}
                    brokerId={profileId}
                    onSelect={handleSearchSelect}
                    onClear={handleSearchClear}
                  />
                </div>
              </div>
              {quotesError && (
                <p className="text-xs text-red-600">
                  {quotesError?.message || 'Failed to load quotes.'}
                </p>
              )}
            </div>

            {isQuotesLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Card key={i} className="overflow-hidden">
                    <CardContent className="p-5 space-y-4">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-11 w-11 rounded-full" />
                        <div className="flex-1 space-y-2">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-24" />
                        </div>
                      </div>
                      <Skeleton className="h-12 w-full" />
                      <div className="flex gap-2">
                        <Skeleton className="h-5 w-20" />
                      </div>
                      <Skeleton className="h-10 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : quotesData?.length === 0 ? (
              <Card className="max-w-md mx-auto !bg-transparent !border-0 !shadow-none">
                <CardContent className="p-12 text-center space-y-4">
                  <img src={EmptyState} alt="No quotes" className="w-30 h-30 mx-auto" />
                  <div>
                    <h3 className="text-md font-semibold text-gray-900 mb-2">
                      {isSearchActive ? 'No quotes found' : 'No quotes created yet'}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {isSearchActive
                        ? 'Try a different search criteria.'
                        : activeFilter === 'all'
                          ? <>Add aircraft to your shortlist to compare <br /> options and send a quote to your client.</>
                          : `No ${activeFilter} quotes at the moment.`}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-4">
                  {quotesData?.map((quote) => (
                    <QuoteCard key={quote.id} quote={quote} onViewQuote={handleViewQuote} />
                  ))}
                </div>
                {hasNextPage && (
                  <div className="flex justify-center mt-8">
                    <button
                      type="button"
                      onClick={() => fetchNextPage()}
                      disabled={isFetchingNextPage}
                      className={`group relative inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm shadow-sm transition-all duration-200 ${
                        isFetchingNextPage
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-primary-600 text-white hover:bg-primary-700 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm'
                      }`}
                    >
                      {isFetchingNextPage ? (
                        <>
                          <RiLoader4Line className="w-4 h-4 animate-spin" />
                          <span>Loading more…</span>
                        </>
                      ) : (
                        <>
                          <span>Load More</span>
                          <RiArrowDownLine className="w-4 h-4 transition-transform group-hover:translate-y-0.5" />
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quote Details Drawer */}
      <QuoteDetailsDrawer
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        quoteId={selectedQuoteId}
      />
    </div>
  );
};

export default QuotesList;